/* eslint-disable no-nested-ternary */
import { MouseEvent } from 'react';
import { Grid, Button, Box, useMediaQuery, Typography, Theme } from '@mui/material';
import {
  CONVERSION_CTA_LABELS,
  RATE_DURATION_ABBREVIATIONS,
  SEARCH_RESULT_TYPE,
} from '@/constants';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';

import { SEOSegmentationPageType, UsStateCode } from '@/__generated__/globalTypes';
import { seoSegmentationListingPage_seoSegmentationDetails_SEOSegmentationDetailsSuccess_segmentationDetails_crossSell as CrossSell } from '@/__generated__/seoSegmentationListingPage';

export interface SeoConversionBoxProps {
  city?: string | null;
  crossSell?: CrossSell | null;
  enrollmentURL?: string;
  header?: string | null;
  pageType?: SEOSegmentationPageType;
  resultType?: keyof typeof SEARCH_RESULT_TYPE;
  serviceId?: string;
  state?: UsStateCode | null;
}

const SeoConversionBox = ({
  city,
  crossSell,
  enrollmentURL,
  header,
  pageType,
  resultType,
  serviceId,
  state,
}: SeoConversionBoxProps) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const isCCProvider =
    typeof resultType !== 'undefined' &&
    SEARCH_RESULT_TYPE[resultType] === SEARCH_RESULT_TYPE.PROVIDER &&
    serviceId === 'CHILDCARE' &&
    crossSell;
  const avgPayRates = crossSell?.avgPayRates;

  const handleCareButton = (e: MouseEvent) => {
    const amplitudeData = {
      cta_location: 'header',
      cta_text: (e.target as HTMLElement).textContent?.toString(),
      cta_type: 'button',
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  const renderHeading = () => {
    if (isCCProvider) {
      return (
        `Find other affordable childcare options` +
        `${pageType === 'LOCAL' ? ` near you in ${city}, ${state}` : ` in your area`}`
      );
    }
    return header;
  };

  const renderCtaBtns = () => {
    if (isCCProvider) {
      return avgPayRates?.map(({ title, url, avgPayRate }) => {
        return (
          <Box key={title} minWidth="200px" mx={1.5} mt={1.5}>
            <Button
              component="a"
              href={url}
              variant="contained"
              size="large"
              fullWidth
              color="secondary"
              data-testid="conversion-cta-link"
              onClick={handleCareButton}>
              {title} ${parseFloat(avgPayRate.rate.amount).toFixed(2)}/
              {RATE_DURATION_ABBREVIATIONS[avgPayRate.rateDuration]}
            </Button>
          </Box>
        );
      });
    }

    return CONVERSION_CTA_LABELS.map((item) => (
      <Box key={item.intent} minWidth="144px" mt={1.5} mx={1}>
        <Button
          component="a"
          href={`${enrollmentURL}&intent=${item.intent}`}
          variant="contained"
          color="secondary"
          size="medium"
          sx={{ fontSize: '16px' }}
          fullWidth
          data-testid="conversion-cta-link"
          onClick={handleCareButton}
          rel="nofollow">
          {item.label}
        </Button>
      </Box>
    ));
  };

  return (
    <Grid
      container
      bgcolor={(theme) => theme.palette.care.grey[100]}
      py={{ xs: 3, sm: 5 }}
      px={0}
      ml="auto">
      <Grid item xs={12}>
        <Typography mb={1} textAlign="center" component="h3" variant="h2">
          {renderHeading()}
        </Typography>
      </Grid>
      <Box
        display="flex"
        justifyContent="center"
        flexWrap={!isMobile ? 'nowrap' : 'wrap'}
        flexBasis="100%">
        {renderCtaBtns()}
      </Box>
    </Grid>
  );
};

export default SeoConversionBox;
